import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useProcessing } from "@/context/ProcessingContext";
import { StatusBadge } from "@/pages/Dashboard";
import { ProcessingView } from "@/components/product/ProcessingView";
import { ErrorView } from "@/components/product/ErrorView";
import { MediaPanel } from "@/components/product/MediaPanel";
import { ListingFields } from "@/components/product/ListingFields";
import { ArrowLeft } from "lucide-react";

export default function ProductReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { start, update, stop } = useProcessing();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "", tags: "", gtin: "" });
  const [coverChoice, setCoverChoice] = useState("cover");
  const [metaPrompt, setMetaPrompt] = useState("/metaads ");
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const pollRef = useRef(null);

  const hydrate = useCallback((p) => {
    setProduct(p);
    setForm({
      title: p.title || "",
      description: p.description || "",
      category: p.category || "",
      tags: (p.tags || []).join(", "),
      gtin: p.gtin || "",
    });
    setCoverChoice(p.images?.cover_choice || "cover");
  }, []);

  const fetchProduct = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/${id}`);
      return data;
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      return null;
    }
  }, [id]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const p = await fetchProduct();
      if (!mounted || !p) return;
      hydrate(p);
      if (p.status === "processing") {
        start(p.progress_message || "Solange está trabalhando...");
        pollRef.current = setInterval(async () => {
          const np = await fetchProduct();
          if (!np) return;
          if (np.status === "processing") {
            update(np.progress_message || "Quase lá...");
          } else {
            clearInterval(pollRef.current);
            stop();
            hydrate(np);
            if (np.status === "ready") toast.success("Prontinho! Seu anúncio está pronto pra revisar. 🐾");
            if (np.status === "error") toast.error("Tivemos um problema ao gerar. Tente novamente.");
          }
        }, 2500);
      }
    })();
    return () => { mounted = false; if (pollRef.current) clearInterval(pollRef.current); stop(); };
  }, [id, fetchProduct, hydrate, start, update, stop]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        gtin: form.gtin, cover_choice: coverChoice,
      };
      const { data } = await api.put(`/products/${id}`, payload);
      hydrate(data);
      toast.success("Alterações salvas!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [form, coverChoice, id, hydrate]);

  const publish = useCallback(async () => {
    await save();
    try {
      const { data } = await api.post(`/products/${id}/publish`);
      hydrate(data);
      toast.success("Marcado como publicado! Copie os dados pra Shopee. 🚀");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [save, id, hydrate]);

  const runMetaAds = useCallback(async () => {
    const prompt = metaPrompt.replace(/^\/metaads/i, "").trim();
    setMetaLoading(true);
    start("Criando o criativo pro Meta Ads...");
    try {
      const { data } = await api.post(`/products/${id}/metaads`, { prompt });
      hydrate(data);
      setCoverChoice("metaads");
      toast.success("Criativo Meta Ads gerado!");
      setMetaOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMetaLoading(false);
      stop();
    }
  }, [metaPrompt, id, hydrate, start, stop]);

  const retry = useCallback(async () => {
    await api.post(`/products/${id}/generate`);
    window.location.reload();
  }, [id]);

  const copy = useCallback((text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  }, []);

  let content;
  if (notFound) {
    content = (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-[#9c5f27]">Produto não encontrado</h1>
        <button onClick={() => navigate("/painel")} className="btn-orange mt-6 px-6 py-3 rounded-full font-semibold">Voltar ao painel</button>
      </div>
    );
  } else if (!product) {
    content = null;
  } else if (product.status === "processing") {
    content = <ProcessingView product={product} />;
  } else if (product.status === "error") {
    content = <ErrorView product={product} onRetry={retry} />;
  } else {
    content = (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="listing-review-container">
        <button onClick={() => navigate("/painel")} className="flex items-center gap-1.5 text-sm text-[#8c735f] hover:text-[#9c5f27]" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Voltar ao painel
        </button>
        <div className="mt-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#9c5f27]">Revise seu anúncio</h1>
            <p className="mt-1 text-[#5c4634] text-sm">Edite o que quiser antes de copiar pra Shopee.</p>
          </div>
          <StatusBadge status={product.status} />
        </div>
        <div className="mt-6 grid lg:grid-cols-2 gap-8">
          <MediaPanel
            product={product} coverChoice={coverChoice} setCoverChoice={setCoverChoice}
            metaOpen={metaOpen} setMetaOpen={setMetaOpen} metaPrompt={metaPrompt} setMetaPrompt={setMetaPrompt}
            runMetaAds={runMetaAds} metaLoading={metaLoading}
          />
          <ListingFields form={form} setForm={setForm} saving={saving} onSave={save} onPublish={publish} onCopy={copy} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-warm">
      <Navbar />
      {content}
    </div>
  );
}
